import {PencilIcon} from '@heroicons/react/24/solid';
import Alert from '../Alert';
import LoadingSpinner from '../LoadingSpinner';
import MapEditorPerson from './MapEditorPerson';
import {useGetPlotQuery} from '/src/redux/plotsApi';
import useAccessLevel from '/src/hooks/useAccessLevel';

const formConfig = {
    autoFocus: true,
    fields: [
        {
            key: 'section',
            title: 'Section',
            type: 'text',
            placeholder: 'X',
            required: true,
            max: 1,
        },
        {
            key: 'lot',
            title: 'Lot Number',
            type: 'number',
            placeholder: '000',
            required: true,
            min: 0,
        },
        {
            key: 'lotPart',
            title: 'Lot Letter',
            type: 'text',
            placeholder: 'X',
            required: false,
            max: 1,
        },
        {
            key: 'notes',
            title: 'Notes',
            type: 'textarea',
            placeholder: '...',
            required: false,
            max: 1024,
        },
        {
            key: 'sizeNotes',
            title: 'Size Notes',
            type: 'textarea',
            placeholder: '...',
            required: false,
            max: 1024,
        },
        {
            key: 'sqf',
            title: 'SQF',
            type: 'number',
            placeholder: '0',
            required: false,
            min: 0,
        },
        {
            key: 'price',
            title: 'Price',
            type: 'number',
            placeholder: '0',
            required: false,
            min: 0,
            step: 0.01,
        },
    ]
}

const skipDetails = [
    'section','lot','lotPart'
]

export default function MapEditorDetailsView({_id, onEditPlot, onEditPeople}){
    const { data: details, error, isLoading } = useGetPlotQuery(_id, {skip: !_id});

    const canEditOccupants = useAccessLevel('visitor');
    const canEditPlots = useAccessLevel('staff');

    return (
        <div>
            {(error && (
                <Alert className='border-t-red-600'><p>{error.message || 'Failed to load'}</p></Alert>
            )) || (!details && (
                <Alert><p>No Entry</p></Alert>
            )) || (isLoading && (
                <Alert><LoadingSpinner className='w-6' /></Alert>
            )) || (
                <div>
                    <div className="relative">
                        <h4 className='text-xl font-light mb-4'>Plot Information</h4>
                        {canEditPlots && onEditPlot && (
                            <button title="Edit Details" onClick={()=>onEditPlot()} className="btn p-2 absolute top-0 right-0">
                                <PencilIcon className="w-6" />
                            </button>
                        )}
                        <p className="text-2xl">
                            <span className="font-bold mr-2" title="Section">{details.section}</span>
                            <span title="Lot">{details.lot}{details.lotPart}</span>
                        </p>
                        <hr className="my-4" />
                        {formConfig.fields.filter(field=>!skipDetails.includes(field.key)).map(field=>(
                            <div key={field.key} className="mb-4">
                                <p className="font-bold">{field.title}</p>
                                <p className="text-xl ml-2">{details[field.key] || 'N/A'}</p>
                            </div>
                        ))}
                    </div>
                    
                    <hr className='my-4' />

                    <div className="relative">
                        <h4 className='text-xl font-light mb-4'>Occupancy Information</h4>
                        {canEditOccupants && onEditPeople && (
                            <button title="Edit Ownership" onClick={()=>onEditPeople()} className="btn p-2 absolute top-0 right-0">
                                <PencilIcon className="w-6" />
                            </button>
                        )}
                        <h4 className="font-bold">Occupants</h4>
                        {(details.plotOccupants && details.plotOccupants.length > 0 && details.plotOccupants.map(person=>(
                            <div key={person._id}>
                                {(canEditOccupants && onEditPeople && (
                                    <button className="w-full p-2 hover:bg-gray-200" onClick={()=>onEditPeople(person._id)}>
                                        <MapEditorPerson _id={person._id} plotId={_id} />
                                    </button>
                                )) || (
                                    <div className="w-full p-2">
                                        <MapEditorPerson _id={person._id} plotId={_id} />
                                    </div>
                                )}
                            </div>
                        ))) || (
                            <p className="text-xl ml-2">N/A</p>
                        )}        
                    </div>

                    <hr className='my-4' />

                    <div className="relative">
                        <h4 className='text-xl font-light mb-4'>Ownership Information</h4>
                        {canEditPlots && onEditPeople && (
                            <button title="Edit Ownership" onClick={()=>onEditPeople()} className="btn p-2 absolute top-0 right-0">
                                <PencilIcon className="w-6" />
                            </button>
                        )}
                        <div className="mb-4">
                            <p className="font-bold">Date Purchased</p>
                            <p className="text-xl ml-2">{details.year? `${details.month || 'XX'}/${details.day || 'XX'}/${details.year || 'XXXX'}` : 'N/A'}</p>
                        </div>
                        <h4 className="font-bold">Owners</h4>
                        {(details.plotOwners && details.plotOwners.length > 0 && details.plotOwners.map(person=>(
                            <div key={person._id}>
                                {(canEditPlots && onEditPeople && (
                                    <button className="w-full p-2 hover:bg-gray-200" onClick={()=>onEditPeople(person._id)}>
                                        <MapEditorPerson _id={person._id} plotId={_id} />
                                    </button>
                                )) || (
                                    <div className="w-full p-2">
                                        <MapEditorPerson _id={person._id} plotId={_id} />
                                    </div>
                                )}
                            </div>
                        ))) || (
                            <p className="text-xl ml-2">N/A</p>
                        )}        
                    </div>
                </div>
            )}
        </div>
    )
}